import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import { ButtonFooterLayout, ContinueButton, SkipButton } from "#/components/carrot-plans/shared/ButtonFooter"
import { FormattedMessage, useIntl } from "react-intl"
import { Form, FormTextField } from "@carrotfertility/carotene-core-x"
import { RoutingFlowTitle } from "#/components/carrot-plans/shared"
import {
  useCheckRoutingInfoQueryCache,
  hasQuestionBeenSkipped
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useMemberProviderSelectionQuery } from "#/components/carrot-plans/hooks/useMemberProviderSelection"
import { useObGynNameStepHandlers } from "#/components/carrot-plans/hooks/useMemberProviderSelectionNameSteps"
import { CenteredLoadingIndicator } from "../../../views/molecules/Molecules"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { sanitizeNameInput } from "#/components/carrot-plans/utils/providerSelectionUtils"

/**
 * OBGYN name step. Fetches routing and member provider selection, derives default first/last name and wasSkipped,
 * uses useObGynNameStepHandlers for save/skip. Empty first + last name is treated as skip. Shows loading until
 * the member provider selection query is ready; auto-advances if the step was skipped or names are already saved.
 */
function NameOfObGynStep(): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const header = intl.formatMessage({ defaultMessage: "What is the name of your OBGYN?" })

  const routingData = useCheckRoutingInfoQueryCache()
  const routingInfoId = routingData?.routingInfoId
  const memberProviderSelectionQuery = useMemberProviderSelectionQuery(routingInfoId)

  const isObGynSelectionLoading = memberProviderSelectionQuery.isLoading || memberProviderSelectionQuery.isFetching
  const obGynSelection = memberProviderSelectionQuery.data ?? null
  const wasSkipped = hasQuestionBeenSkipped("nameOfObGyn", routingData)
  const { createSaveHandler, createSkipHandler } = useObGynNameStepHandlers({
    routingInfoId,
    selectionWhenReady: obGynSelection,
    wasSkipped
  })

  const savedFirstName = obGynSelection?.providerFirstName || ""
  const savedLastName = obGynSelection?.providerLastName || ""
  const shouldAutoAdvance = wasSkipped || !!savedFirstName || !!savedLastName
  const { send } = useStateMachine(isObGynSelectionLoading ? undefined : shouldAutoAdvance ? "" : null)

  if (isObGynSelectionLoading) return <CenteredLoadingIndicator />

  const saveProviderSelection = createSaveHandler(send)
  const onSkip = createSkipHandler(send)

  async function onClickContinue(formValues: { firstName: string; lastName: string }): Promise<void> {
    const firstName = sanitizeNameInput(formValues?.firstName)
    const lastName = sanitizeNameInput(formValues?.lastName)

    // If both fields are empty, treat as skip
    if (!firstName && !lastName) {
      await onSkip()
      return
    }

    await saveProviderSelection(firstName, lastName)
  }

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={header}
        formElements={
          <Form onSubmit={onClickContinue} defaultValues={{ firstName: savedFirstName, lastName: savedLastName }}>
            <Stack spacing={theme.spacing(theme.tokens.spacing.sm)} direction="row">
              <FormTextField
                inputProps={{ "data-testid": "provider-first-name" }}
                name="firstName"
                label={intl.formatMessage({ defaultMessage: "First name" })}
              />
              <FormTextField
                inputProps={{ "data-testid": "provider-last-name" }}
                name="lastName"
                label={intl.formatMessage({ defaultMessage: "Last name" })}
              />
            </Stack>
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

export { NameOfObGynStep }
